import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../../../services/api';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * Acciones masivas sobre la fase de los expedientes marcados (06-reparto §5):
 *   - dar de baja (Fallido, Ilocalizable, Sin datos de contacto o Cancelled);
 *   - pasar a recobro, lo mismo que el botón de la ficha;
 *   - volver a AR, solo el admin y como corrección: lo que sale de AR no vuelve
 *     por decisión del cliente (Edgar, 15/10/2026).
 * Todas piden confirmación. Lo que el servidor no puede hacer lo salta y lo dice.
 */
export const DEACTIVATION_STATES = ['Fallido', 'Ilocalizable', 'Sin datos de contacto', 'Cancelled'];

const ACCIONES = {
  deactivate: {
    ruta: '/ar-exit/deactivate',
    titulo: (n, estado) => `¿Dar de baja ${n} expedientes como ${expedientStatusLabel(estado)}?`,
    texto:
      'Dejarán de gestionarse y de facturarse: se cancelan sus tareas y lo que la IA tenía pendiente. Si más adelante vuelves a ponerlos en un estado activo, contarán otra vez en la cartera de ese mes.',
    boton: 'Sí, dar de baja',
    hecho: (n, estado) => `${n} expedientes dados de baja como ${expedientStatusLabel(estado)}`,
  },
  escalate: {
    ruta: '/ar-exit/escalate',
    titulo: n => `¿Pasar ${n} expedientes a recobro?`,
    texto:
      'Los gestionará ClaimUp como recobro extrajudicial y entrarán en «En curso». Sus tareas abiertas y las propuestas de la IA pendientes dejan de ser vuestras.',
    boton: 'Sí, pasar a recobro',
    hecho: n => `${n} expedientes pasados a recobro`,
  },
  return: {
    ruta: '/ar-exit/return-to-ar',
    titulo: n => `¿Devolver ${n} expedientes a AR?`,
    texto:
      'Es una corrección: los expedientes vuelven a AR como si hubieran entrado ahí, los gestiona el cliente y cuentan en su facturación AR de este mes. Solo los de recobro de empresas con AR activado.',
    boton: 'Sí, devolver a AR',
    hecho: n => `${n} expedientes devueltos a AR`,
  },
};

function BulkDeactivateBar({ selectedIds, isAdmin = false, onDone, onClear }) {
  const [toState, setToState] = useState('Fallido');
  const [accion, setAccion] = useState(null);
  const [sending, setSending] = useState(false);
  const n = selectedIds.length;

  const ejecutar = async () => {
    const a = ACCIONES[accion];
    setSending(true);
    try {
      const body = accion === 'deactivate' ? { expedient_ids: selectedIds, to_state: toState } : { expedient_ids: selectedIds };
      const { data } = await api.post(a.ruta, body);
      const { done, skipped } = data.data;
      toast.success(a.hecho(done.length, toState));
      if (skipped.length) {
        toast.info(`${skipped.length} no se pudieron: ${[...new Set(skipped.map(s => s.reason))].join(', ')}`, {
          autoClose: 8000,
        });
      }
      setAccion(null);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  if (!n) return null;
  const actual = accion ? ACCIONES[accion] : null;

  return (
    <>
      <div
        className='mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900'
        role='region'
        aria-label='Acciones masivas'
      >
        <span className='font-medium'>{n} seleccionados</span>
        <label className='flex items-center gap-2'>
          Dar de baja como
          <select
            aria-label='Estado de baja'
            value={toState}
            onChange={e => setToState(e.target.value)}
            className='rounded-lg border-amber-300 text-sm'
          >
            {DEACTIVATION_STATES.map(s => (
              <option key={s} value={s}>
                {expedientStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <button
          type='button'
          onClick={() => setAccion('deactivate')}
          className='px-3 py-1.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700'
        >
          Dar de baja
        </button>
        <button
          type='button'
          onClick={() => setAccion('escalate')}
          className='px-3 py-1.5 rounded-lg border border-amber-400 text-amber-900 font-medium hover:bg-amber-100'
        >
          Pasar a recobro
        </button>
        {isAdmin && (
          <button
            type='button'
            onClick={() => setAccion('return')}
            className='px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-100'
          >
            Volver a AR
          </button>
        )}
        <button type='button' onClick={onClear} className='px-3 py-1.5 rounded-lg text-amber-800 hover:bg-amber-100'>
          Quitar selección
        </button>
      </div>

      {actual && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4' role='dialog' aria-modal='true'>
          <div className='bg-white rounded-lg shadow-lg max-w-md w-full p-6 space-y-4'>
            <h3 className='text-lg font-semibold text-gray-800'>{actual.titulo(n, toState)}</h3>
            <p className='text-sm text-gray-600'>{actual.texto}</p>
            <div className='flex justify-end gap-2'>
              <button
                type='button'
                onClick={() => setAccion(null)}
                disabled={sending}
                className='px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100'
              >
                Cancelar
              </button>
              <button
                type='button'
                onClick={ejecutar}
                disabled={sending}
                className='px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60'
              >
                {sending ? 'Un momento…' : actual.boton}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BulkDeactivateBar;
